import type { FC, ReactNode } from 'react'
import type { SiteLink } from '@/types'

import { buttonStyles } from '@/lib/button-styles'
import SmartLink from '@/components/generals/smart-link'
import CreamTexture from '@/components/landing/cream-texture'
import RevealWords from '@/components/landing/reveal-words'
import Sparkle from '@/components/landing/sparkle'

interface CtaBandProps {
    id: string
    title: string
    text?: string
    primary: SiteLink
    secondary?: SiteLink
}

// The apricot closing band of an inner page. It runs straight into the apricot footer, like the home page.
const CtaBand: FC<CtaBandProps> = ({ id, title, text, primary, secondary }): ReactNode => {
    return (
        <section id={id} aria-labelledby={`${id}-title`} className='relative overflow-hidden bg-apricot'>
            <CreamTexture variant='apricot' className='opacity-70' />

            {/* Sparkles stay in the band's padding, clear of the headline. */}
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <Sparkle className='absolute left-[9%] top-[14%] size-4 text-white motion-safe:animate-softly-twinkle' />
                <Sparkle className='absolute bottom-[12%] right-[11%] size-3 text-cocoa/40 motion-safe:animate-softly-twinkle [animation-delay:1.4s]' />
            </div>

            <div className='relative mx-auto max-w-[1440px] px-5 py-24 text-center lg:px-10 lg:py-32'>
                <RevealWords
                    id={`${id}-title`}
                    text={title}
                    className='mx-auto max-w-[11em] text-balance type-display'
                />

                {text && (
                    <p className='mx-auto mt-5 max-w-[32rem] text-[16px] leading-relaxed text-cocoa/85 lg:text-[17px]'>
                        {text}
                    </p>
                )}

                <div className='mt-9 flex flex-wrap justify-center gap-3'>
                    <SmartLink href={primary.href} className={buttonStyles('primary', 'lg')}>
                        {primary.label}
                    </SmartLink>

                    {secondary && (
                        <SmartLink href={secondary.href} className={buttonStyles('secondary', 'lg')}>
                            {secondary.label}
                        </SmartLink>
                    )}
                </div>
            </div>
        </section>
    )
}

export default CtaBand
